// sonos.js — client seam for the Sonos Control API proxy (api/sonos).
//
// Mirrors lib/aiden.js: gated on VITE_SONOS_PROXY. When off, listGroups() returns
// [] (so the UI simply shows no Sonos targets) and control calls throw a clear
// error. When on, every call hits the server-side proxy — no Sonos creds or
// tokens ever touch the browser.
//
// The player uses this to become a *remote control* when a Sonos group is the
// active output: transport buttons call these instead of the local <audio>.

export function sonosProxyEnabled() {
  return import.meta.env.VITE_SONOS_PROXY === "true";
}

async function call(path, { method = "GET", body } = {}) {
  if (!sonosProxyEnabled()) {
    throw new Error("Sonos is not configured (VITE_SONOS_PROXY is off).");
  }
  const res = await fetch(`/api/sonos/${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Sonos ${method} ${path} failed (${res.status})`);
  return data;
}

/** List speaker groups (whole-house first). Returns [] when the proxy is off. */
export async function listGroups() {
  if (!sonosProxyEnabled()) return [];
  try {
    const { groups } = await call("groups");
    return groups || [];
  } catch {
    return [];
  }
}

/** Start playing a stream URL on a group (whole-house playback). */
export function playOnGroup(groupId, streamUrl, title) {
  return call("play", { method: "POST", body: { groupId, streamUrl, title } });
}

export function pauseGroup(groupId) {
  return call("pause", { method: "POST", body: { groupId } });
}

export function resumeGroup(groupId) {
  return call("resume", { method: "POST", body: { groupId } });
}

/** Fully stop (suspends the session — reliable teardown, unlike a bare pause). */
export function stopGroup(groupId) {
  return call("stop", { method: "POST", body: { groupId } });
}

export function setGroupVolume(groupId, volume) {
  return call("volume", { method: "POST", body: { groupId, volume } });
}

export function muteGroup(groupId, muted) {
  return call("mute", { method: "POST", body: { groupId, muted } });
}

/** Seek to an absolute position (milliseconds) within the current item. */
export function seekGroup(groupId, positionMillis) {
  return call("seek", { method: "POST", body: { groupId, positionMillis } });
}

/** Poll while a group is the active output to reflect play/pause + position. */
export function getGroupStatus(groupId) {
  return call(`status?groupId=${encodeURIComponent(groupId)}`);
}
